import React, { useEffect, useMemo, useState, useCallback } from "react";

import {
  TextField,
  Typography,
  Box,
  Grid,
  Autocomplete,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  Divider,
  Alert,
} from "@mui/material";

import { useQuery } from "react-query";

import APIService from "../services/api";
import { Bank } from "../types/bank.types";
import { FinansPortalen } from "../types/deal.types";

import { useDebounce } from "../utils/debounce";

import { extractDealAndCreateDiplayName } from "../utils/dealToGraphDataMapper";

import { getSavingsAmount } from "../utils/savingsCalculator";

import MortgageGraph, { MortgageDataItem } from "./RateGraph/MortgageGraph";

import NokAmountInput from "./FormControls/NokAmountInput";

import InterestInput from "./FormControls/InterestInput";

import SavingsDisplay from "./Resultsdisplay/SavingsDisplay";

import SubResultDisplay from "./Resultsdisplay/SubResultDisplay";

import missingFieldAlertImg from "../assets/images/coinInterest.svg";

import highLoanAlertImg from "../assets/images/Low_Prospect.svg";

import locked from "../assets/images/clinsjPadlock.svg";

// === Constants ===

const QUERY_CONFIG = {
  staleTime: Infinity,
  cacheTime: Infinity,
  refetchOnWindowFocus: false,
  refetchOnReconnect: false,
} as const;

const DEFAULT_VALUES = {
  LOAN_AMOUNT: 4_000_000,
  PROPERTY_VALUE: 5_500_000,
  INTEREST_RATE: 6.4, // Percentage value (e.g., 6.4 = 6.4%)
  DURATION: 1,
  MAX_LTV: 85,
  RATE_THRESHOLD: 0.0001,
};

const ALERTS: Record<string, AlertInfo> = {
  MISSING_FIELDS: {
    message: "Det virker som du har glemt å fylle ut et felt. Vennligst fyll ut alle feltene.",
    image: missingFieldAlertImg,
    altText: "Ikon som indikerer manglende felt",
  },
  HIGH_LTV: {
    message: "Du har en høy belåningsgrad. ",
    image: highLoanAlertImg,
    altText: "Ikon som indikerer høy belåningsgrad",
  },
  NO_ALERT: {
    message: "",
    image: locked,
    altText: "Standard ikon for ingen varsler",
  },
};

// === Types ===

interface ResultData {
  minInterestRate: number;
  maxInterestRate: number;
  minSavings: number;
  betterOffers: number;
}

interface AlertInfo {
  message: string;
  image: string;
  altText: string;
}

// === Utility Functions ===

const filterDeals = (
  deals: FinansPortalen[],
  loanAmount: number,
  propertyValue: number,
  interestRate: number, // Percentage (e.g., 6.4 = 6.4%)
  isUnder34: boolean
): FinansPortalen[] => {
  const ltv = (loanAmount / propertyValue) * 100;

  return deals
    .filter((deal) => deal.Restr_MaxLTV >= ltv)
    .filter((deal) => deal.Int_Nominal < interestRate)
    .filter((deal) => deal.InterestFixedYears === 0)
    .filter((deal) => deal.Restr_MinLendingAmount <= loanAmount)
    .filter((deal) => deal.Restr_MaxLendingAmount >= loanAmount)
    .filter((deal) => !deal.Restr_HolidayHome)
    .filter((deal) => !deal.Restr_GreenLoan)
    .filter((deal) => {
      // Only exclude if Conditions is a string and equals "other" (case-insensitive)
      if (typeof deal.Conditions === 'string') {
        return deal.Conditions.trim().toLowerCase() !== 'other';
      }
      // If null, undefined, or not a string → include
      return true;
    })
    .filter((deal) => {
      // Filter: Only include deals with "Nationwide" in LendingArea (case-insensitive)
      // null, undefined, or empty string means nationwide (no restriction)
      const area = deal.LendingArea;
      // If no LendingArea specified, treat as nationwide (no restriction)
      if (area == null || area === '') {
        return true;
      }
      // If it's a string, check if it contains "Nationwide" (case-insensitive)
      if (typeof area === 'string') {
        return area.toLowerCase().includes('nationwide');
      }
      // For arrays, check if any element contains "Nationwide"
      if (Array.isArray(area)) {
        return (area as any[]).some((item: any) => 
          typeof item === 'string' && item.toLowerCase().includes('nationwide')
        );
      }
      // For other types, exclude
      return false;
    })
    .filter((deal) => !deal.Restr_Membership || deal.Restr_Membership === null)
    .filter((deal) => (isUnder34 ? true : !deal.Restr_FirstHomeLoan && !deal.Restr_YouthLoan))
    .sort((a, b) => a.Int_Nominal - b.Int_Nominal);
};

// === Main Component ===

const Calculator: React.FC = () => {
  const [loanAmount, setLoanAmount] = useState(DEFAULT_VALUES.LOAN_AMOUNT);
  const [propertyValue, setPropertyValue] = useState(DEFAULT_VALUES.PROPERTY_VALUE);
  const [selectedBank, setSelectedBank] = useState<Bank | null>(null);
  const [interestRate, setInterestRate] = useState(DEFAULT_VALUES.INTEREST_RATE);
  const [isUnder34, setIsUnder34] = useState(true);
  const [duration, setDuration] = useState(DEFAULT_VALUES.DURATION);
  const [alert, setAlert] = useState<AlertInfo>(ALERTS.NO_ALERT);
  const [filteredDeals, setFilteredDeals] = useState<FinansPortalen[]>([]);
  const [displayData, setDisplayData] = useState<MortgageDataItem[]>([]);
  const [resultData, setResultData] = useState<ResultData>({
    minInterestRate: 0,
    maxInterestRate: 0,
    minSavings: 0,
    betterOffers: 0,
  });

  // Debounce inputs to avoid excessive recalculation
  const debouncedLoanAmount = useDebounce(loanAmount, 500);
  const debouncedPropertyValue = useDebounce(propertyValue, 500);
  const debouncedInterestRate = useDebounce(interestRate, 500);
  const debouncedIsUnder34 = useDebounce(isUnder34, 500);

  const { data: rawBanks, isLoading: banksLoading, error: banksError } = useQuery<Bank[]>(
    "banks",
    APIService.fetchBanks,
    QUERY_CONFIG
  );

  const { data: deals, error: dealsError, isLoading: dealsLoading } = useQuery<FinansPortalen[]>(
    "deals",
    APIService.fetchDeals,
    {
      ...QUERY_CONFIG,
      onError: (error) => {
        console.error('Failed to fetch deals:', error);
      },
      onSuccess: (data) => {
        console.log('Deals fetched successfully:', data?.length || 0, 'deals');
      },
    }
  );


  // Set banks and default bank
  const banks = useMemo(() => {
    if (!rawBanks) return [];
    return rawBanks
      .filter((bank) => bank.f_tilbyr_boliglan)
      .sort((a, b) => a.sort_order - b.sort_order);
  }, [rawBanks]);

  useEffect(() => {
    if (banks.length > 0 && !selectedBank) {
      const defaultBank = banks.find((bank) => bank.title === "DNB Bank ASA") ?? banks[0];
      setSelectedBank(defaultBank);
      if (defaultBank?.CurrentRate) {
        setInterestRate(defaultBank.CurrentRate);
      }
    }
  }, [banks, selectedBank]);

  // Update interest rate when bank changes
  useEffect(() => {
    if (selectedBank?.CurrentRate) {
      setInterestRate(selectedBank.CurrentRate);
    }
  }, [selectedBank]);

  // Compute results and alerts
  useEffect(() => {
    if (banksLoading || dealsLoading) return;
    if (!deals) {
      console.warn('Deals data is not available');
      return;
    }

    // Validate inputs
    if (!debouncedLoanAmount || !debouncedPropertyValue || !debouncedInterestRate) {
      setAlert(ALERTS.MISSING_FIELDS);
      setFilteredDeals([]);
      setDisplayData([]);
      return;
    }

    const ltv = (debouncedLoanAmount / debouncedPropertyValue) * 100;
    if (ltv > DEFAULT_VALUES.MAX_LTV) {
      setAlert(ALERTS.HIGH_LTV);
      setFilteredDeals([]);
      setDisplayData([]);
      return;
    }

    // Filter deals - interestRate and Int_Nominal are both percentages
    const filtered = filterDeals(
      deals,
      debouncedLoanAmount,
      debouncedPropertyValue,
      debouncedInterestRate,
      debouncedIsUnder34
    );
    setFilteredDeals(filtered);

    // Convert Int_Nominal from percentage to decimal for calculations
    const minInterestRateDecimal =
      filtered.length > 0 ? filtered[0].Int_Nominal * 0.01 : debouncedInterestRate * 0.01;
    const interestRateDecimal = debouncedInterestRate * 0.01;

    // If no better deals found, still show graph with user's rate
    if (filtered.length === 0) {
      setResultData({
        minInterestRate: interestRateDecimal,
        maxInterestRate: interestRateDecimal + 0.002,
        minSavings: 0,
        betterOffers: 0,
      });
      setAlert(ALERTS.NO_ALERT);
      return;
    }

    // Check if the best deal is too close to user's rate
    if (minInterestRateDecimal + DEFAULT_VALUES.RATE_THRESHOLD >= interestRateDecimal) {
      // Still show the graph with available deals
      const maxInterestRateDecimal = minInterestRateDecimal + 0.002;
      const minSavings = Math.round(
        getSavingsAmount(interestRateDecimal, minInterestRateDecimal, debouncedLoanAmount, duration)
      );
      setResultData({
        minInterestRate: minInterestRateDecimal,
        maxInterestRate: maxInterestRateDecimal,
        minSavings,
        betterOffers: filtered.length,
      });
      setAlert(ALERTS.NO_ALERT);
      return;
    }

    const maxInterestRateDecimal = minInterestRateDecimal + 0.002;
    const minSavings = Math.round(
      getSavingsAmount(interestRateDecimal, minInterestRateDecimal, debouncedLoanAmount, duration)
    );

    setResultData({
      minInterestRate: minInterestRateDecimal,
      maxInterestRate: maxInterestRateDecimal,
      minSavings,
      betterOffers: filtered.length,
    });
    setAlert(ALERTS.NO_ALERT);
  }, [deals, banksLoading, dealsLoading, debouncedLoanAmount, debouncedPropertyValue, debouncedInterestRate, debouncedIsUnder34, duration]);

  // Update display data - always show at least user's rate
  useEffect(() => {
    if (!interestRate) return;
    
    const data: MortgageDataItem[] = [
      ...filteredDeals.map(extractDealAndCreateDiplayName),
      { name: "Din rente", ownInterestRate: interestRate },
    ].sort(
      (a, b) =>
        (a.interestRate ?? a.ownInterestRate!) - (b.interestRate ?? b.ownInterestRate!)
    );

    // Always set displayData, even if empty (will show user's rate)
    setDisplayData(data.length > 0 ? data : [{ name: "Din rente", ownInterestRate: interestRate }]);
  }, [filteredDeals, interestRate]);

  if (banksLoading || dealsLoading) {
    return (
      <Typography variant="h6" textAlign="center">
        Laster...
      </Typography>
    );
  }

  if (banksError) {
    return (
      <Alert severity="error" sx={{ maxWidth: 600, mx: "auto", mt: 4 }}>
        Kunne ikke laste bankdata. Vennligst prøv igjen senere.
      </Alert>
    );
  }

  // Show warning but continue if deals fail - user can still see their rate
  if (dealsError) {
    console.error('Deals API error:', dealsError);
  }

  return (
    <Box
      sx={{
        display: "block",
        "@media (min-width: 700px)": {
          display: "flex",
        },
        "@media (min-width: 800px)": {
          marginTop: "0px",
        },
      }}
    >
      {/* Form Section */}
      <Box
        component="form"
        noValidate
        autoComplete="off"
        marginTop="0px"
        sx={{
          textAlign: "center",
          "& .MuiTextField-root": { margin: "5px auto", display: "flex" },
          "& .MuiSlider-root": {
            margin: "5px auto",
            width: "85%",
            maxWidth: "420px",
          },
          "& .MuiFormControl-root": {
            margin: "0px auto",
            width: "100%",
            maxWidth: "420px",
          },
          "@media (min-width: 700px)": {
            textAlign: "left",
            "& .MuiSlider-root": { margin: "5px 10px" },
            width: "30vw",
          },
        }}
        aria-label="Boliglånskalkulator skjema"
      >
        <NokAmountInput
          label="Hvor mye har du i lån?"
          value={loanAmount}
          min={500000}
          max={10000000}
          step={50000}
          onValueChange={setLoanAmount}
        />
        <NokAmountInput
          label="Hvor mye er boligen din verdt?"
          value={propertyValue}
          min={500000}
          max={20000000}
          step={50000}
          onValueChange={setPropertyValue}
        />
        <FormControl component="fieldset">
          <Typography variant="h6" sx={{ fontWeight: "550", marginLeft: "0px" }}>
            Er du under 34 år?
          </Typography>
          <RadioGroup
            row
            aria-label="isUnder34"
            name="row-radio-buttons-group"
            value={isUnder34 ? "yes" : "no"}
            onChange={(event) => setIsUnder34(event.target.value === "yes")}
            sx={{
              marginRight: "auto",
              marginLeft: "auto",
              "@media (min-width: 700px)": {
                marginRight: "auto",
                marginLeft: "22px",
              },
            }}
          >
            <FormControlLabel value="yes" control={<Radio />} label="Ja" />
            <FormControlLabel value="no" control={<Radio />} label="Nei" />
          </RadioGroup>
        </FormControl>
        <Typography
          variant="h6"
          sx={{
            fontWeight: "550",
            marginLeft: "0px",
            marginBottom: "10px",
            "@media (max-width: 700px)": { marginInline: "5px" },
          }}
        >
          Hvor er du kunde?
        </Typography>
        <Autocomplete
          value={selectedBank}
          onChange={(event, newValue) => {
            setSelectedBank(newValue);
          }}
          options={banks || []}
          getOptionLabel={(option) => option.title || ""}
          isOptionEqualToValue={(option, value) => option.bankId === value.bankId}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Velg bank"
              variant="outlined"
              required
            />
          )}
          sx={{
            marginRight: "5px",
            marginLeft: "5px",
            "@media (min-width: 700px)": {
              marginRight: "5px",
              marginLeft: "0px",
            },
          }}
          aria-describedby="bank-selection"
        />
        <InterestInput
          label="Hva har du i rente i dag?"
          interestRate={interestRate}
          setInterestRate={setInterestRate}
          minRate={3}
          maxRate={8}
          step={0.01}
        />
      </Box>

      {/* Results Section */}
      <Grid item xs={12} md={6}>
        <Box
          sx={{
            paddingTop: 0,
            textAlign: "center",
            "@media (min-width: 700px)": {
              width: "70vw",
            },
            "@media (max-width: 700px)": {
              padding: "20px 0px 0px 0px",
            },
            height: "100%",
          }}
        >
          {alert.message ? (
            <Box sx={{}}>
              <Typography
                variant="h6"
                sx={{
                  paddingLeft: "10px",
                  fontWeight: "500",
                  fontSize: "20px",
                  "@media (min-width: 700px)": {
                    textAlign: "left",
                    marginLeft: "40px",
                  },
                  "@media (min-width: 800px)": {
                    fontSize: "24px",
                    fontWeight: "500",
                    marginTop: "0px",
                  },
                }}
              >
                {alert.message}
              </Typography>
              {alert.image && (
                <img
                  src={alert.image}
                  alt={alert.altText}
                  style={{
                    height: "400px",
                    width: "auto",
                    marginTop: "25px",
                    maxWidth: "75%",
                    marginInline: "auto",
                  }}
                />
              )}
            </Box>
          ) : (
            <Box
              sx={{
                "@media (min-width: 700px)": {
                  paddingLeft: "10px",
                },
              }}
            >
              <SavingsDisplay
                savingsAmount={resultData.minSavings}
                duration={duration}
                setDuration={setDuration}
              />
              <Divider variant="middle" />
              <SubResultDisplay
                key="better-offers"
                label="Bedre lån på markedet"
                infoText="Antall tilbud i markedet med samme krav som ditt eksisterende lån som har bedre vilkår enn ditt nåværende lån."
                value={resultData.betterOffers.toString()}
                suffix="Lån"
              />
              <SubResultDisplay
                key="recommended-rate"
                label="Din rente bør ligge på"
                infoText="Basert på lånebeløp, sikkerhet og alder bør du ha en rente rundt dette. "
                value={(resultData.minInterestRate * 100).toFixed(2).toString()}
                suffix="%"
              />

              <Box
                marginTop="20px"
                sx={{
                  width: "100%",
                  overflow: "hidden",
                  "@media (min-width: 800px)": { width: "98%" },
                }}
              >
                {displayData && displayData.length > 0 ? (
                  <MortgageGraph
                    mortgageData={displayData}
                    xAxisTitle="Tilbud sortert etter pris"
                    height={280}
                  />
                ) : (
                  <Typography variant="body1">Laster graf...</Typography>
                )}
              </Box>
            </Box>
          )}
        </Box>
      </Grid>
    </Box>
  );
};

export default Calculator;
